import {
    ErrorContainer,
    ImagePreviewStack,
    ImagesPreviewsContainer,
} from "../../PostForm.styles";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { DraggableImage, UploadImagesInput } from "../../PostFormModal.types";
import { SortableList } from "../../../../SortableList/SortableList";
import { usePostFormPollMediaFilesUploadInputStore } from "./PostFormPollMediaFilesUploadInput.store";
import { useUploadPostCommentImages } from "../../../../Modals/CommentFormModal/useUploadPostCommentImages";
import { ImagesIcon } from "@icons/ImagesIcon";
import {
    ImageUploadTeaser,
    SortableListContainer,
    TeaserImagesContainer,
} from "./ImagesOptions.styles";
import { ImageItem } from "./PollImageItem";

export const MAX_IMAGES_COUNT = 4;

export function ImagesOptions({ allowPollEdit }: { allowPollEdit: boolean }) {
    const { images, setImages, setIsUploading } =
        usePostFormPollMediaFilesUploadInputStore();
    const abortController = useRef<AbortController>(new AbortController());
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const effectAbortController = abortController.current;
        return () => {
            effectAbortController.abort();
        };
    }, []);

    const uploadPostImagesParam: UploadImagesInput = {
        images,
        setImages,
        setIsUploading,
        open: true,
        abortController: abortController.current,
    };
    const {
        resetUpload,
        handleOnRemoveImage,
        handleDragImage,
        inappropriateContentDetected,
        handleOnAddImage,
    } = useUploadPostCommentImages(uploadPostImagesParam);

    const [items, setItems] = useState(
        images.map((image, id) => ({ id: id + 1, image: image })),
    );
    const [isPointerDown, setIsPointerDown] = useState(false);
    const [startXDrag, setStartXDrag] = useState(0);
    const [scrollLeftPosition, setScrollLeftPosition] = useState(0);
    const [dragAndDropInProgress, setDragAndDropInProgress] = useState(false);

    const scrollAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setItems(
            images
                .filter((item) => !item.delete)
                .map((image, id) => ({
                    id: id + 1,
                    image: image,
                })),
        );
    }, [images]);

    const onChange = (newItems: DraggableImage[]) => {
        setItems(newItems);
        handleDragImage(newItems);
    };

    const onDragAndDropChanged = (dragAndDropInProgress: boolean) => {
        setDragAndDropInProgress(dragAndDropInProgress);
        setIsPointerDown(false);
    };

    function startDrag(event: React.PointerEvent): void {
        if (dragAndDropInProgress) {
            return;
        }
        setIsPointerDown(true);
        if (scrollAreaRef.current) {
            scrollAreaRef.current.style.cursor = "pointer";
            scrollAreaRef.current.style.scrollBehavior = "auto";
            setStartXDrag(event.pageX - scrollAreaRef.current.offsetLeft);
            setScrollLeftPosition(scrollAreaRef.current.scrollLeft);
        }
    }

    function endDrag(): void {
        setIsPointerDown(false);
        if (scrollAreaRef.current) {
            scrollAreaRef.current.style.cursor = "initial";
            scrollAreaRef.current.style.scrollBehavior = "smooth";
        }
    }

    function onPointerMove(event: React.PointerEvent): void {
        event.preventDefault();
        if (isPointerDown && scrollAreaRef && scrollAreaRef.current) {
            const x = event.pageX - scrollAreaRef.current.offsetLeft;
            const scroll = x - startXDrag;
            scrollAreaRef.current.scrollLeft = scrollLeftPosition - scroll;
        }
    }
    const [maxImagesReached, setMaxImagesReached] = useState(false);
    const handleSelectNewImages = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        if (e.target.files.length + items.length > MAX_IMAGES_COUNT) {
            setMaxImagesReached(true);
            return;
        }
        setMaxImagesReached(false);
        handleOnAddImage(e);
    };

    const teaserImages = [];
    for (let i = 0; i < 4 - items.length; ++i) {
        teaserImages.push(
            <ImageUploadTeaser
                onClick={() => {
                    inputRef.current?.click();
                }}
            >
                <ImagesIcon
                    color="var(--community-color-transparent-black-20)"
                    width="36px"
                    height="36px"
                />
            </ImageUploadTeaser>,
        );
    }

    return (
        <ImagesPreviewsContainer style={{ margin: "0" }}>
            <ImagePreviewStack
                direction="row"
                ref={scrollAreaRef}
                onPointerDown={startDrag}
                onPointerUp={endDrag}
                onPointerMove={onPointerMove}
                onPointerLeave={endDrag}
                hideGap={true}
            >
                <SortableListContainer
                    style={{
                        flex: items.length,
                    }}
                >
                    <SortableList
                        items={items}
                        onChange={onChange}
                        onDragAndDropChanged={onDragAndDropChanged}
                        renderItem={(item) => (
                            <ImageItem
                                itemKey={item.id}
                                image={item.image}
                                handleOnRemoveImage={handleOnRemoveImage}
                                handleImageUploadRetry={resetUpload}
                                allowPollEdit={allowPollEdit}
                            />
                        )}
                    />
                </SortableListContainer>
                {allowPollEdit && (
                    <TeaserImagesContainer
                        style={{
                            flex: 4 - items.length,
                        }}
                    >
                        {teaserImages.map((teaser, index) => (
                            <Fragment key={index}>{teaser}</Fragment>
                        ))}
                    </TeaserImagesContainer>
                )}
            </ImagePreviewStack>
            {inappropriateContentDetected && (
                <ErrorContainer>
                    Dein Bildbeitrag wurde aufgrund unserer Richtlinien leider
                    abgelehnt.
                </ErrorContainer>
            )}
            {maxImagesReached && (
                <ErrorContainer>
                    Upload von max. 4 Bildern möglich
                </ErrorContainer>
            )}
            <input
                type="file"
                multiple
                max={4}
                accept="image/*, .heic, .heif"
                ref={inputRef}
                onChange={handleSelectNewImages}
                style={{
                    position: "absolute",
                    width: 0,
                    height: 0,
                    top: -9999,
                    left: -9999,
                }}
            />
        </ImagesPreviewsContainer>
    );
}
